// Amplitude analytics integration (runs alongside Statsig)
//
// Keys events to our owned `web_id` as the Amplitude `device_id` (see utils/web-id.ts).
// `user_id` is intentionally left UNSET on the web — it is the Supabase UUID, set
// server-side at purchase and in-app after login. Email is attached only as a user
// property. Funnel events arrive via the central track() in analytics.ts, which
// forwards to trackAmplitude() once this module registers its sink at init —
// including a replay of events buffered while consent was pending (their
// capture-time timestamp is preserved via Amplitude's `time` event option).
import * as amplitude from '@amplitude/analytics-browser';
import { getWebId } from '../utils/web-id';
import { registerAnalyticsSink } from './analytics.js';
import { getAmplitudeConfig } from '../config/env';
import { getEnvironment } from '../utils/environment';
import { getExperimentProperties } from '../config/experiments';

let amplitudeInitialized = false;

/**
 * Initialize Amplitude. Must run AFTER getWebId() is available (post-consent),
 * alongside Statsig, so both analytics tools key on the same owned web_id.
 */
export async function initAmplitude(): Promise<void> {
  if (amplitudeInitialized) return;

  const cfg = getAmplitudeConfig();
  if (!cfg?.apiKey || cfg.apiKey.startsWith('TODO_')) {
    console.warn('Amplitude API key not configured for this environment — skipping init');
    return;
  }

  const deviceId = getWebId();

  try {
    await amplitude.init(cfg.apiKey, undefined, {
      deviceId,
      serverZone: cfg.serverZone,
      instanceName: 'lunai',
      // SPA with a hash router + curated event taxonomy: keep sessions + attribution,
      // but let all funnel signal flow through track() rather than autocaptured DOM/URL
      // events (which would double-count against our screen_view taxonomy).
      autocapture: {
        sessions: true,
        attribution: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
    }).promise;

    // Base user properties, for parity with the Statsig user context.
    const baseProps = new amplitude.Identify();
    baseProps.set('environment', getEnvironment());
    baseProps.set('source', 'web2app');
    // Local A/B arms as user properties (also on every event via the central
    // enricher) — lets Amplitude segment the whole funnel by variant.
    for (const [key, value] of Object.entries(getExperimentProperties())) {
      baseProps.set(key, value);
    }
    amplitude.identify(baseProps);

    amplitudeInitialized = true;

    // Start receiving events forwarded from the central track() — including
    // the replay of events buffered while consent was pending.
    registerAnalyticsSink('amplitude', trackAmplitude);

    console.log(`✅ Amplitude initialized (${cfg.serverZone}, device_id=${deviceId})`);
  } catch (error) {
    console.error('Failed to initialize Amplitude:', error);
  }
}

/**
 * Forward a tracked event to Amplitude. `purchase` is intentionally NOT forwarded —
 * the authoritative purchase/revenue event is emitted server-side (single source of
 * truth), so the client must not double-count it.
 */
export function trackAmplitude(eventName: string, properties: Record<string, any> = {}): void {
  if (!amplitudeInitialized) return;
  if (eventName === 'purchase') return;
  // Preserve the capture-time timestamp (matters for replayed pre-consent
  // events, which would otherwise all cluster at consent time).
  const capturedAt = properties.timestamp ? Date.parse(properties.timestamp) : NaN;
  amplitude.track(eventName, properties, Number.isNaN(capturedAt) ? undefined : { time: capturedAt });
}

/**
 * Attach user properties (e.g. email) to the current anonymous device profile.
 * Does NOT set user_id — user_id is the Supabase UUID, set server-side at purchase
 * and in-app after login.
 */
export function identifyAmplitude(props: Record<string, any>): void {
  if (!amplitudeInitialized) return;
  const identity = new amplitude.Identify();
  for (const [key, value] of Object.entries(props)) {
    if (value != null) identity.set(key, value);
  }
  amplitude.identify(identity);
}
